/* eslint-disable array-callback-return */
import React, { useEffect, useState } from "react";
import { BachmansProduct } from "../../models/product";
import { Box, createStyles, makeStyles, SvgIcon, Theme, useMediaQuery } from "@material-ui/core";
import Slider from "react-slick";
import productService from "../../services/product.service";
import { cloneDeep, find, reject } from "lodash";
import { neutral } from "../../themes/colors";
import appConstants from "../../constants/app.constants";
import { ImageMagnifier } from "../../services/imageMagnifier.service";

export interface ProductImageProps {
  product: BachmansProduct;
  multiSku: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sliderContainer: {
      "& button": {
        pointer: "hover",
        "&.slick-next": {
          marginRight: "55px",
          zIndex: 1,
        },
        "&.slick-prev": {
          marginLeft: "55px",
          zIndex: 1,
        },
        "&.slick-next:before": {
          color: theme.palette.primary.dark,
          fontSize: "30px",
          opacity: 0.9,
        },
        "&.slick-prev:before": {
          color: theme.palette.primary.dark,
          fontSize: "30px",
          opacity: 0.9,
        },
      },
      [theme.breakpoints.up("sm")]: {
        "& button": {
          pointer: "hover",
        },
      },
      "& .slick-dots": {
        bottom: "-20px",
        "& li": {
          margin: 0,
          "&.slick-active button:before": {
            color: theme.palette.primary.dark,
            opacity: 1,
          },
          "& button:before": {
            opacity: 0.75,
            fontSize: 12,
            color: "grey",
          },
        },
      },
    },
    noImage: {
      backgroundColor: neutral.grey_background,
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      display: "flex !important",
      aspectRatio: "1 / 1",
      [theme.breakpoints.down("sm")]: {
        display: "none !important",
      },
    },
    logo: {
      width: 300,
      height: "auto",
      "& path": {
        fill: neutral.disabled,
      },
    },
    drawer: {
      flexBasis: "33.33334%",
      padding: "0 5px 10px",
    },
  })
);

const ProductImages: React.FunctionComponent<ProductImageProps> = (props) => {
  const { product, multiSku } = props;
  const classes = useStyles();
  const [carouselImages, setCarouselImages] = useState<any[]>();
  const [defaultImage, setDefaultImage] = useState<any>();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const settings = {
    dots: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
  };

  useEffect(() => {
    if (product?.xp?.Contentful) {
      const images: any[] = cloneDeep(product?.xp?.Contentful?.Images);
      if (product?.xp?.IsDefaultProduct && images.length > 1 && multiSku) {
        const defImg = productService.GetDefaultImage(product);
        setDefaultImage(defImg);
        const imagesForCarousel = find(images, function (i) {
          return i.title.includes("000000");
        })
          ? reject(images, function (data) {
              return data.url === defaultImage;
            })
          : images;
        setCarouselImages(imagesForCarousel);
      } else {
        setCarouselImages(images);
      }
    } else {
      setCarouselImages([]);
    }
  }, [product, multiSku, defaultImage]);

  return (
    <Slider
      className={!carouselImages || carouselImages.length < 1 ? classes.noImage : classes.sliderContainer}
      {...settings}
    >
      {carouselImages?.map((img: any, indx: number) => {
        if (!isMobile && appConstants.enableimageZoom) {
          return <ImageMagnifier src={img.url} key={indx} alt={img.title} magnifieWidth={250} magnifierHeight={250} />;
        } else {
          return <img src={img.url} key={indx} alt={img.title} />;
        }
      })}
      {(!carouselImages || carouselImages.length < 1) && (
        <Box display="flex !important" flexDirection="column" justifyContent="center" alignItems="center">
          <SvgIcon viewBox="0 0 963 151.1" className={classes.logo}>
            <path d="M84.4 74c21.1 5.5 31.6 17.2 31.6 35.1 0 7.4-1.1 13.2-3.4 17.4-2.3 4.2-4.8 7.5-7.6 10-9.1 8.4-24.9 12.7-47.2 12.7H.1v-10.5c6.7 0 10.8-.6 12.5-1.8 1.6-1.2 2.6-2.4 2.9-3.8.3-1.3.5-2.9.5-4.9v-101c0-4.1-.7-6.7-2.1-7.7-2.5-1.8-7.2-2.7-13.9-2.7V6.5h53c25.7 0 42.5 6.1 50.5 18.3 3.2 4.8 4.8 10.4 4.8 16.8 0 15.1-8 25.9-23.9 32.4zm-34.1-3c17.8-.4 28.6-5.6 32.4-15.6 1.3-3.3 1.9-7 1.9-11 0-8.6-2.5-15.3-7.6-20.1-5-4.9-13.7-7.3-25.9-7.3H37.4v54h12.9zm5.7 67.7c18.9 0 30.4-6.2 34.4-18.5 1.2-3.6 1.8-7.5 1.8-11.8 0-8.8-3.3-15.7-9.9-20.8-6.6-5.1-15.7-7.8-27.3-8.1H37.3v59.2H56zM207 126.4c0 7.5 1.5 11.3 4.5 11.3 2.9 0 4.9-1 6.3-2.9 1.3-1.9 2-5.4 2-10.5h10c.1.8.1 2.1.1 3.9 0 5.7-1.4 10.7-4.3 14.8-3.5 5.3-9 8-16.7 8-4.9 0-9.2-1.4-12.7-4.2s-5.9-6.5-7.2-11c-9.2 10.1-20.8 15.2-34.9 15.2-7.7 0-14.5-2.3-20.2-6.8s-8.6-10.9-8.6-19c0-14.1 10.4-24.5 31.2-31.2 13.2-4.2 21.1-7.1 23.7-8.7s4.3-3.3 5.1-4.9c.8-1.7 1.2-3.6 1.2-5.7 0-3.7-1.8-6.9-5.5-9.4s-8.5-3.8-14.4-3.8-11.1 1.6-15.6 4.8c3.2 2.3 4.8 5.4 4.8 9 0 3.7-1.2 6.6-3.7 8.8-2.4 2.2-5.4 3.3-8.8 3.3-3.5 0-6.3-1-8.6-3.1s-3.4-5-3.4-8.8c0-7.4 3.7-13.4 11.2-18s16.6-6.9 27.3-6.9c19.2 0 30.9 6.2 35.1 18.6 1.4 4.1 2.1 8.9 2.1 14.3v42.9zM152.5 112c-2.8 3.8-4.2 7.9-4.2 12.3s1.2 7.8 3.6 10.1c2.4 2.3 5.4 3.4 9 3.4 7.4 0 13.4-2.6 18.3-7.8 4.8-5.2 7.2-12.4 7.2-21.6V93.8c-4.1 2.1-8.4 4-12.9 5.7s-7.4 2.8-8.5 3.2c-5.6 2.5-9.8 5.6-12.5 9.3zM297.5 68.4c-3.7-4.4-8.8-6.7-15.4-6.7-12.4 0-20.4 7-24.2 21.1-1.3 4.9-1.9 10.9-1.9 17.8 0 12.9 2.5 22.6 7.4 29.1s11.1 9.8 18.6 9.8c5.5 0 10.1-1.6 13.9-4.8s6.4-7.7 7.9-13.6h13c0 7.9-3.5 14.8-10.4 20.8s-16.4 9-28.3 9-22-3.7-30.3-11.1c-9.8-8.7-14.7-21.5-14.7-38.3 0-13.8 4.1-25.7 12.2-35.7 8.1-10.1 20.1-15.1 35.9-15.1 10.3 0 19 2.5 25.9 7.5s10.4 11.5 10.4 19.4c0 3.9-1.3 7.1-3.8 9.8-2.5 2.6-5.7 3.9-9.6 3.9-5.5 0-9.3-2.6-11.4-7.7-.6-1.5-1-3.2-1-5 .2-4.3 2-7.7 5.8-10.2zM384.9 65.1c-10.4 0-18.8 3.2-25.3 9.7v53.9c0 3.9.6 6.4 1.9 7.4 2.2 1.7 6.4 2.6 12.6 2.6v10.5h-48.3v-10.5c5.5 0 8.9-.6 10.3-1.7 1.4-1.1 2.2-2.3 2.6-3.6.3-1.3.5-2.9.5-4.8V25.5c0-4.6-.4-7.5-1.2-8.9-.8-1.4-3-2.1-6.5-2.1h-5.8V4c6 0 10.6-.5 14-1.4s5.8-1.6 7.2-2 3-.6 4.6-.6c3.4 0 5.6 1 6.6 2.9s1.6 5 1.6 9.3v51.4c6.5-5.7 14.4-9.6 23.6-11.7 3-.7 5.9-1 8.8-1 20.6 0 30.8 13.6 30.8 40.9v36.8c0 4.2.6 6.9 1.8 8.2 1.2 1.3 4.5 1.9 9.9 1.9v10.5h-45v-10.5c5.8 0 9.2-.6 10.4-1.8 1.2-1.2 1.9-2.4 2-3.7.2-1.3.3-2.9.3-4.8V93c0-8.8-.8-14.9-2.5-18.2-3.3-6.5-8.3-9.7-14.9-9.7zM568.9 50.9c18.6 0 28 13.6 28 40.8v36.8c0 4.2.6 6.9 1.8 8.2s4.5 1.9 9.9 1.9v10.5h-45v-10.5c5.8 0 9.2-.6 10.4-1.8s1.9-2.4 2-3.7c.2-1.3.3-2.9.3-4.8V93c0-8.9-.7-15-2.1-18.2-1.4-3.3-3.2-5.7-5.4-7.3-2.2-1.6-4.7-2.4-7.5-2.4-5.1 0-9.9 1-14.6 3-4.6 2-8.3 4.7-11 8 .9 4.6 1.3 9.8 1.3 15.6v36.8c0 4.2.6 6.9 1.8 8.2s4.5 1.9 9.9 1.9v10.5h-45v-10.5c5.8 0 9.2-.6 10.4-1.8s1.9-2.4 2-3.7c.2-1.3.3-2.9.3-4.8V93c0-8.9-.7-15-2.1-18.2-1.4-3.3-3.2-5.7-5.4-7.3-2.2-1.6-4.7-2.4-7.5-2.4-4.8 0-9.5.9-13.9 2.8-4.4 1.8-8.1 4.3-10.9 7.3v53.6c0 4 .6 6.5 1.9 7.5 2.3 1.6 6.4 2.5 12.5 2.5v10.5h-48.2v-10.5c5.5 0 8.9-.6 10.3-1.7 1.4-1.1 2.2-2.3 2.6-3.6.3-1.3.5-2.9.5-4.8V76.9c0-4.6-.4-7.5-1.2-8.9-.8-1.4-3-2.1-6.5-2.1h-5.8V55.4c6 0 10.7-.5 14.1-1.4 3.5-.9 5.9-1.6 7.4-2s3-.6 4.8-.6c3.2 0 5.3 1 6.3 2.9s1.5 5 1.5 9.3v.2c6.5-5.6 14.3-9.6 23.6-11.8 3-.7 6-1 8.8-1 11.4 0 19.3 5.1 23.7 15.2 7.1-6.6 15.7-11.2 25.7-13.8 3.3-1 6.8-1.5 10.3-1.5zM696.4 126.4c0 7.5 1.5 11.3 4.5 11.3 2.9 0 4.9-1 6.3-2.9 1.3-1.9 2-5.4 2-10.5h10c.1.8.1 2.1.1 3.9 0 5.7-1.4 10.7-4.3 14.8-3.5 5.3-9 8-16.7 8-4.9 0-9.2-1.4-12.7-4.2s-5.9-6.5-7.2-11c-9.2 10.1-20.8 15.2-34.9 15.2-7.7 0-14.5-2.3-20.2-6.8s-8.6-10.9-8.6-19c0-14.1 10.4-24.5 31.2-31.2 13.2-4.2 21.1-7.1 23.7-8.7 2.6-1.6 4.3-3.3 5.1-4.9.8-1.7 1.2-3.6 1.2-5.7 0-3.7-1.8-6.9-5.5-9.4s-8.5-3.8-14.4-3.8-11.1 1.6-15.6 4.8c3.2 2.3 4.8 5.4 4.8 9 0 3.7-1.2 6.6-3.7 8.8-2.4 2.2-5.4 3.3-8.8 3.3-3.5 0-6.3-1-8.6-3.1s-3.4-5-3.4-8.8c0-7.4 3.7-13.4 11.2-18s16.6-6.9 27.3-6.9c19.2 0 30.9 6.2 35.1 18.6 1.4 4.1 2.1 8.9 2.1 14.3v42.9zM641.8 112c-2.8 3.8-4.2 7.9-4.2 12.3s1.2 7.8 3.6 10.1c2.4 2.3 5.4 3.4 9 3.4 7.4 0 13.4-2.6 18.3-7.8 4.8-5.2 7.2-12.4 7.2-21.6V93.8c-4.1 2.1-8.4 4-12.9 5.7s-7.4 2.8-8.5 3.2c-5.5 2.5-9.7 5.6-12.5 9.3zM782.6 65.1c-10.4 0-18.8 3.2-25.3 9.7v53.9c0 3.9.6 6.4 1.9 7.4 2.2 1.7 6.4 2.6 12.6 2.6v10.5h-48.3v-10.5c5.5 0 8.9-.6 10.3-1.7 1.4-1.1 2.2-2.3 2.6-3.6.3-1.3.5-2.9.5-4.8V76.9c0-4.6-.4-7.5-1.2-8.9s-3-2.1-6.5-2.1h-5.8V55.4c6 0 10.6-.5 14-1.4s5.8-1.6 7.2-2 3-.6 4.6-.6c3.4 0 5.6 1 6.6 2.9s1.6 5 1.6 9.3c6.8-5.7 14.7-9.6 23.6-11.6 3-.7 5.9-1 8.8-1 20.6 0 30.8 13.6 30.8 40.8v36.8c0 4.2.6 6.9 1.8 8.2s4.5 1.9 9.9 1.9v10.5h-45v-10.5c5.8 0 9.2-.6 10.4-1.8s1.9-2.4 2-3.7c.2-1.3.3-2.9.3-4.8V93c0-8.8-.8-14.9-2.5-18.2-3.3-6.5-8.2-9.7-14.9-9.7zM909.8 65.4c-3.2-3.2-8.5-4.9-15.9-4.9s-12.3 2.7-14.9 8c-.7 1.5-1 3.3-1 5.2s.8 3.8 2.3 5.6c1.5 1.8 3.5 3.5 5.9 5.1s5.1 3.2 8.1 4.7 6.1 3.2 9.4 4.9c3.2 1.8 6.7 3.6 10.3 5.6 13.3 7.2 19.9 15.2 19.9 24s-3.4 15.5-10.3 20.3-15.9 7.1-27.3 7.1-20.7-2-28-6.1c-7.4-4.1-11-9.3-11-15.7 0-3.5 1.2-6.4 3.5-8.8s5.3-3.6 8.8-3.6c3.6 0 6.5 1.1 8.9 3.4 2.3 2.3 3.5 4.9 3.5 8 0 3.1-1.1 5.7-3.2 7.9 5.1 2.9 11.4 4.4 18.9 4.4s12.8-1.7 15.8-5.2c1.3-1.4 1.9-3.2 1.9-5.4 0-5.3-5.8-10.8-17.5-16.5-8.1-4-14.2-7.1-18.3-9.4-13-7.4-19.5-16.2-19.5-26.6 0-8 3.3-14.4 9.9-19.2 6.6-4.8 15.1-7.2 25.4-7.2s18.9 1.9 25.9 5.8c7 3.8 10.5 8.8 10.5 14.9 0 3.6-.9 6.2-2.6 7.9-3 3-5.8 4.5-8.6 4.5-4.4 0-7.8-1.1-10-3.4-2.3-2.3-3.4-4.8-3.4-7.8s.9-5.5 2.6-7.5z" />
            <path d="M830.7 60.9s33.2-.3 36.8-59.7c-52.4 28.2-36.8 59.7-36.8 59.7z" />
            <path d="M942.9 138.9h-4v-1.1h9.7v1.1h-4v9.1h-1.7v-9.1zM961.1 143.6c-.1-1.4-.2-3.1-.2-4.4h-.1c-.4 1.2-1 2.5-1.6 3.9l-2.3 5h-1.3l-2.1-4.9c-.6-1.4-1.1-2.8-1.5-4 0 1.3-.1 3-.3 4.5l-.3 4.4h-1.6l.9-10.2h2.2l2.2 5c.5 1.3 1 2.4 1.3 3.5h.1c.3-1 .8-2.2 1.4-3.5l2.3-5h2.2l.8 10.2h-1.6l-.5-4.5z" />
          </SvgIcon>
        </Box>
      )}
    </Slider>
  );
};

export default ProductImages;
